import { Rectangle, Texture, TextureSource } from 'pixi.js';
import gfxRegistry from './objectGfx.json';
import bulletRegistry from './bulletGfx.json';
import type { Game } from '../Game';
import { KnyttPoint } from '../../engine/KnyttPoint';
import { TILE_SIZE } from '../../render/Tileset';

export interface MuffParams {
  directionChange: number; // 0 Random, 1 TowardsJuni, 2 AwayFromJuni
  directionChangeTime: number;
  speedChangeTime: number;
  initialSpeed: number;
  speedValues: number[] | null;
  deceleration: number;
  vertical: boolean;
  xBorder: number;
  // RollerMuff (bank3 3,2/3,8): periodic "charge" — speed jumps to 24 towards Juni,
  // plays "roll" until it hits something, then resumes normal patrol.
  roller?: { interval: number };
  // GhostWalker (bank12 GhostWalker.cs): speeds up + flickers faster while phasing
  // through a wall (its own Area2D senses overlap with solid ground).
  ghostWalker?: { wallSpeed: number };
  // DistanceMuff (common/DistanceMuff.cs): halts speed/direction changes while Juni
  // is within range (reuses the spiker DistanceMod extraction).
  haltDist?: { dist: number; method: number };
  // RunThing (bank14 BigRunThing/SmallRunThing): fight-or-flight — chase Juni within
  // safeDistance at attackSpeed/attackDeceleration, else flee at a random patrol speed.
  runThing?: {
    safeDistance: number;
    attackSpeed: number;
    normalDeceleration: number;
    attackDeceleration: number;
    interval: number;
  };
  // Insect (14,11): AttackTimer re-rolls speed + re-aims (runTo: 0 random / 1 towards
  // / 2 away) on top of the normal Muff patrol.
  insect?: { runTo: number; interval: number };
  // Jelly (RedJelly 14,1): hops — each anim cycle boosts speed to jellySpeed, picking
  // a fresh random direction at rest.
  jelly?: { speed: number };
  // WalkingShooter (Ninja/DarkRandomizer/StrangeRunner/HomingRunner): a periodic
  // walk→prepare→shoot→aftershot cycle around the object's own already-extracted
  // emitter. `limitBullets`'s Selector-based concurrency cap is not replicated
  // (same simplification as the multi-cannon Selector elsewhere).
  walkingShooter?: {
    bulletsCount: number;
    waitForTimer: boolean;
    startWithShoot: boolean;
    interval: number;
    shotDelay: number;
  };
  // WallNinja (Left|RightNinja, Left|Right(Super)Stranger): a vertical wall-climber
  // that periodically fires a directional volley — either all `shotDirections` at
  // once, or one random pick from them (`randomDirection`). MMF2 direction wheel
  // values, same convention as `EmitterConfig.dir`.
  wallNinja?: {
    bulletScene: string;
    bulletVelocity: number;
    bulletGravity: number;
    shotDirections: number[];
    randomDirection: boolean;
    tx: number;
    ty: number;
    interval: number;
    sound?: string;
  };
}

export interface AnimEntry {
  sheet: string;
  frames?: number[][];
  fps?: number;
  // Bullets only (BaseBullet.disappear(collide:true)): one-shot impact animation + sound.
  disappear?: AnimEntry;
  hitSound?: string;
}

export interface EmitterConfig {
  bullet: string;
  tx: number;
  ty: number;
  dir: number;
  vel: number;
  gravity: number;
  decel: number;
  pinball: boolean;
  interval: number;
  sound?: string; // shot-sound base name (Sfx/<sound>.flac), played once per volley
  // Cartesian velocity (UpCannon/WaterMonster: VelocityX/Y/Gravity in px/s). Each axis is
  // a [min,max] range; `*D` = discrete (a coin-flip between the two endpoints).
  cartesian?: { vx: number[]; vxD: boolean; vy: number[]; vyD: boolean; gravity: number };
  velPerI?: number; // MMF2 velocity gains this per burst-counter step (RobotCannon: +i)
  dirPerI?: boolean; // ShockDisk: DirectionMMF2 = i → 17-bullet radial fan, one direction per burst index
  // Bee (2,9/2,10): each tick fires with 1/chance probability, one bullet angled by
  // its walk direction (MMF2 19/27 + rand(3), spawn offset ±5).
  bee?: { chance: number };
  // TrapFire (6,6): fire along the true angle from Juni (+10 y bias) through the trap
  // (BaseBullet negates its velocity vector, so the bullet flies toward Juni).
  aimAway?: boolean;
  // BadFlything/HomingFlything: each burst index takes its own MMF2 direction, so one
  // rate:0 burst fires the whole multi-direction volley at once.
  dirTable?: number[];
  // StuffShooter (6,7/6,9): a fixed raw Godot angle ((up?1:3)·π/2 → straight up/down).
  fixedRawDir?: number;
  // StuffShooter/SuperShot/HomingFlower one-shot flourish, played when it fires.
  launch?: AnimEntry;
  // TrapFlower family: the "open" pose, played forward entering gate range, backward leaving.
  gateAnim?: AnimEntry;
  // DropFlything/QuadFlything/TentacleFlything: every `period`, fire the whole
  // `directions` volley once per `rate` tick, `ticks` times (a dense radial spray).
  dropVolley?: { directions: number[]; ticks: number; rate: number; period: number };
  burst?: { count: number; period: number; rate: number }; // Cannon: count bullets/cycle
  disappearStopped?: boolean; // vanish once decelerated to a stop (WhiteExplosion ShockWave)
  aimX?: { divisor: number; base: number; rand: number }; // HomingCannon: fire vel leads Juni's x-distance
  gate?: { dist: number; method: number }; // only fire while Juni within range (DropCrawler/MultiShot)
}

export interface PathConfig {
  points: number[][];
  speed: number;
  // PathCreature (Fish1-5, Birdie/Birdie1/Birdie2): non-looping paths ping-pong;
  // at each end (or loop completion) FlipTimer pauses then flips the sprite, and
  // RunTimer pauses (playing "stop") before the run resumes. Absent for FlyBot's
  // plain continuous loop.
  creature?: { loop: boolean; flipTime: number | null; runTime: number | null };
}

export interface SpikerConfig {
  openDist: number;
  closeDist: number;
  method: number; // 0=euclidean, 1=manhattan, 2=by-x
  sheet: string;
  open: number[][]; // open-animation regions; [0]=retracted … [last]=extended
  fps: number;
  idle: number[] | null; // retracted frame region, or null → use open[0]
  area: number[] | null; // [x,y,w,h] tile-local deadly rect, or null
  delayDeath?: boolean; // BigSpiker: only deadly once fully extended (grace while opening)
  offset?: number[]; // [x,y] sprite draw offset for multi-tile sheets (BigSpiker)
  silent?: boolean; // playSound=false (HiddenSpiker) → no extend/retract SFX
}

export interface ObjectEntry extends AnimEntry {
  additive?: boolean;
  // LiquidPool 1,5/1,10/1,12/1,22: an optional "Halo" AnimatedSprite drawn behind the
  // water (LiquidPool.cs plays both; Halo is node index 1, the main sprite index 2).
  halo?: AnimEntry & { offset: number[]; alpha: number; additive: boolean };
  anims?: Record<string, AnimEntry>;
  ghost?: boolean;
  deadly?: boolean;
  solid?: boolean;
  organic?: boolean;
  muff?: MuffParams;
  emitter?: EmitterConfig;
  path?: PathConfig;
  spiker?: SpikerConfig;
  // LookMuff/GhostLookMuff (bank3 LookMuff family, bank12 AnotherGhost/AnotherGhost2):
  // stationary, flips its sprite to face Juni's apparent x position.
  lookMuff?: boolean;
  // BackgroundBird (bank2 2,31): cosmetic screen-wrapping parallax decoration.
  backgroundBird?: boolean;
  // SelfDropper (bank6 6,8): harmless until Juni is near-x, then drops straight down
  // (shrinking hitbox mid-fall) and stays lethal once landed.
  selfDropper?: boolean;
  // GesturesObject (common/GesturesObject.cs): idle-cycles a random non-"_"-prefixed
  // animation every [minTime,maxTime]s, optionally flipping to a random side. `anims`
  // resolves its own SpriteFrames directly since GesturesObject scenes override the
  // instanced base's AnimatedSprite, bypassing the normal default-animation resolution.
  gestures?: { minTime: number; maxTime: number; bidirectional: boolean; anims: Record<string, AnimEntry> };
  // TimedFlower (bank9 9,2) / SmallTimedFlower (9,5): toggles open (playing "open"
  // forward) then fires one bullet per shotInterval while open; SmallTimedFlower's
  // `shots` caps the burst before auto-closing (plays "open" backwards), TimedFlower's
  // null `shots` keeps firing until the next toggle.
  toggleShoot?: { toggleInterval: number; shotInterval: number; shots: number | null };
  // BaseEater (bank6 Eater 6,5 / bank12 GhostEater 12,10): plays this one-shot "eat"
  // animation + a Chomp sound on contact, instead of just vanishing generically.
  eat?: AnimEntry;
  // BaseWaterMonster (WaterEye 17,3 / WaterMonster 18,6): every `interval` seconds,
  // play "prepare", fire `bullets` bullets at once through the object's emitter,
  // then play "aftershot". The `pickOne` group-rotation Selector is not replicated
  // (same simplification as the multi-cannon Selector elsewhere).
  waterMonster?: { prepare: AnimEntry; aftershot?: AnimEntry; bullets: number; interval: number };
  // BuzzFlyer (Flything family): random 8-direction buzz flight, re-picking direction
  // on collision or when about to leave the screen.
  buzz?: { speed: number; strength: number; border: number };
  // Fly (2,3/2,4): startled into jittery buzz-flight when Juni is near; lands on solids.
  fly?: boolean;
  // Firefly (2,8/2,13/2,23): drifting random walk — speed/direction/opacity all buzz.
  firefly?: {
    limitSpeed: number;
    resetSpeed: number;
    idleSpeed: number;
    runSpeed: number;
    speedBuzz: number;
    speedOffset: number;
    directionBuzz: number;
    opacityBuzz: number;
    idle: AnimEntry | null;
    fly: AnimEntry | null;
    run: AnimEntry | null;
  };
  // Humbird (2,25): faces Juni; periodically arcs up 112px and back over 2.9s.
  humbird?: { interval: number };
  // Spider run-bursts (BigSpider 4,9 / TriLegSpider 4,14): horizontal dash until a
  // wall/edge, then flip + wait for the next trigger (radar band / timer + chance).
  spiderRun?: {
    speed: number;
    radar: boolean;
    interval: number;
    chance: number;
    walk: AnimEntry | null;
    idle: AnimEntry | null;
  };
  // ShadowWitch (5,1): anger meter driven by Juni's facing — deadly + visible only angry.
  shadowWitch?: boolean;
  // ProximityBlock (15,6 real / 15,7 fake): fades in as Juni approaches (132px).
  proximity?: { real: boolean };
  // FalseBlock (15,8-11): looks like a wall but isn't; fades out while Juni overlaps.
  falseBlock?: boolean;
  // FadeBlock (15,25): decorative sin-wave alpha shimmer.
  fadeBlock?: boolean;
  // Crumble (15,37/38): solid platform that collapses after Juni lands on it.
  crumble?: {
    delay: number;
    activate: AnimEntry | null;
    destroy: AnimEntry | null;
    disappear: AnimEntry | null;
  };
  // MachinePart4 (8,14): pick a random frame at load, never animate.
  randomFrame?: boolean;
  // FlySpike (2,20/2,21): dashes horizontally at 150-200px/s until a wall, then flips
  // and waits 1.6-3.2s before dashing back.
  flySpike?: { facingRight: boolean };
  // ObjectSelector.cs (pickOne timers / IsObjectSelected fire gates): only one member
  // of a same-id group fires per synchronized round.
  selector?: boolean;
  // Godot sprite transforms that differ from the port's default (art top-left at the
  // tile top-left). `spriteOffset`: a non-centered sprite's position+offset (Eater's
  // art at (0,-24) spans the tile above). `spriteCenter`: a *centered* sprite's
  // position+offset — Godot re-centres every frame around it, so mixed-size animations
  // (Elemental: 24px idle + 96px launch) stay centred; ported as anchor(.5,.5) there.
  spriteOffset?: number[];
  spriteCenter?: number[];
}

const BULLETS = bulletRegistry as Record<string, AnimEntry>;

/** Load a bullet type's frames + frame size, or null. */
export async function loadBulletGfx(
  game: Game,
  bulletType: string,
): Promise<{
  frames: Texture[];
  fps: number;
  w: number;
  h: number;
  disappear?: { frames: Texture[]; fps: number };
  hitSound?: string;
} | null> {
  const entry = BULLETS[bulletType];
  if (!entry) return null;
  const frames = await sliceFrames(game, entry.sheet, entry.frames);
  if (!frames) return null;
  const r = entry.frames?.[0];
  // BaseBullet.disappear(collide:true): "disappear" animation + HitPlayer sound on impact.
  let disappear: { frames: Texture[]; fps: number } | undefined;
  if (entry.disappear) {
    const df = await sliceFrames(game, entry.disappear.sheet, entry.disappear.frames);
    if (df) disappear = { frames: df, fps: entry.disappear.fps ?? 8 };
  }
  return {
    frames,
    fps: entry.fps ?? 8,
    w: r ? r[2] : 8,
    h: r ? r[3] : 8,
    disappear,
    hitSound: entry.hitSound,
  };
}

export interface LoadedAnim {
  frames: Texture[];
  fps: number;
}

const REGISTRY = gfxRegistry as Record<string, ObjectEntry>;

export function getEntry(objectId: KnyttPoint): ObjectEntry | null {
  return REGISTRY[objectId.key] ?? null;
}

/**
 * Recursively collects every `sheet` path referenced anywhere in an entry
 * (the main sheet plus any nested AnimEntry — anims/halo/eat/waterMonster/
 * firefly/spiderRun/crumble/gestures/emitter.launch/emitter.gateAnim/spiker),
 * for prefetching without duplicating each behaviour's own loading logic.
 */
export function collectEntrySheets(entry: unknown, out: Set<string>): void {
  if (!entry || typeof entry !== 'object') return;
  for (const [key, value] of Object.entries(entry as Record<string, unknown>)) {
    if (key === 'sheet' && typeof value === 'string') out.add(value);
    else if (value && typeof value === 'object' && !Array.isArray(value)) collectEntrySheets(value, out);
  }
}

/** Slice frame textures for a sheet + regions (or a 24px grid fallback). */
async function sliceFrames(
  game: Game,
  sheet: string,
  regions: number[][] | undefined,
): Promise<Texture[] | null> {
  const tex = await game.assets.getTexture(sheet);
  if (!tex) return null;
  const src = tex.source as TextureSource;
  const frames: Texture[] = [];
  if (regions && regions.length > 0) {
    for (const [x, y, w, h] of regions) {
      frames.push(new Texture({ source: src, frame: new Rectangle(x, y, w, h) }));
    }
  } else {
    const cols = Math.max(1, Math.floor(src.width / TILE_SIZE));
    const rows = Math.max(1, Math.floor(src.height / TILE_SIZE));
    if (cols * rows <= 1) frames.push(tex);
    else
      for (let r = 0; r < rows; r++)
        for (let c = 0; c < cols; c++)
          frames.push(
            new Texture({
              source: src,
              frame: new Rectangle(c * TILE_SIZE, r * TILE_SIZE, TILE_SIZE, TILE_SIZE),
            }),
          );
  }
  return frames.length ? frames : null;
}

/** Slice explicit regions from a sheet (e.g. a spiker's open-animation frames). */
export async function loadFrames(
  game: Game,
  sheet: string,
  regions: number[][],
): Promise<Texture[] | null> {
  return sliceFrames(game, sheet, regions);
}

/** Load a named animation (e.g. 'Idle', 'Save', 'walk') for an object. */
export async function loadAnim(
  game: Game,
  objectId: KnyttPoint,
  animName: string,
): Promise<LoadedAnim | null> {
  const entry = REGISTRY[objectId.key];
  if (!entry) return null;
  const a = entry.anims?.[animName];
  if (!a) return null;
  const frames = await sliceFrames(game, a.sheet, a.frames);
  return frames ? { frames, fps: a.fps ?? 10 } : null;
}

/** Slice a standalone AnimEntry (e.g. one of a GesturesObject's named animations). */
export async function loadEntryAnim(game: Game, entry: AnimEntry): Promise<LoadedAnim | null> {
  const frames = await sliceFrames(game, entry.sheet, entry.frames);
  return frames ? { frames, fps: entry.fps ?? 10 } : null;
}

/** Load the object's default animation + metadata. */
export async function loadObjectFrames(
  game: Game,
  objectId: KnyttPoint,
): Promise<{
  frames: Texture[];
  fps: number;
  additive: boolean;
  spriteOffset?: number[];
  spriteCenter?: number[];
} | null> {
  const entry = REGISTRY[objectId.key];
  if (!entry || !entry.sheet) return null;
  const frames = await sliceFrames(game, entry.sheet, entry.frames);
  if (!frames) return null;
  return {
    frames,
    fps: entry.fps ?? 10,
    additive: entry.additive ?? false,
    spriteOffset: entry.spriteOffset,
    spriteCenter: entry.spriteCenter,
  };
}
